import type { DateFormat } from '@heidi/schema/ml-scribe';
import type { Locale } from 'date-fns';
import { format, formatDistanceToNow, isValid, parse, parseISO } from 'date-fns';
import {
  ca,
  da,
  de,
  enAU,
  es,
  fi,
  fr,
  it,
  ja,
  ko,
  nb,
  nl,
  pl,
  pt,
  sv,
  zhCN,
  zhTW,
} from 'date-fns/locale';
import { ar as dateFnsAr } from 'date-fns/locale/ar';

import { getSupportedLocaleOrDefault, SupportedLocale } from './i18n/supported-locales';

/**
 * Maps app locale codes to date-fns locale objects.
 * Falls back to enAU (Australian English) if locale not found.
 */
const dateFnsLocaleMap: Record<SupportedLocale, Locale> = {
  [SupportedLocale.En]: enAU,
  [SupportedLocale.EnPs]: enAU,
  [SupportedLocale.EsEs]: es,
  [SupportedLocale.DeDe]: de,
  [SupportedLocale.FrFr]: fr,
  [SupportedLocale.ItIt]: it,
  [SupportedLocale.JaJp]: ja,
  [SupportedLocale.ZhCn]: zhCN,
  [SupportedLocale.ZhTw]: zhTW,
  [SupportedLocale.CaEs]: ca,
  [SupportedLocale.DaDk]: da,
  [SupportedLocale.NlNl]: nl,
  [SupportedLocale.FiFi]: fi,
  [SupportedLocale.NbNo]: nb,
  [SupportedLocale.PlPl]: pl,
  [SupportedLocale.PtPt]: pt,
  [SupportedLocale.SvSe]: sv,
  [SupportedLocale.KoKr]: ko,
  [SupportedLocale.ArSa]: dateFnsAr,
};

/**
 * Gets the date-fns Locale object for a given app locale.
 * @param locale - The app locale code (e.g., 'en', 'fr', 'sv')
 * @returns The corresponding date-fns Locale object, defaults to enAU
 */
export const getDateFnsLocale = (locale: string): Locale => {
  return (
    dateFnsLocaleMap[
      getSupportedLocaleOrDefault(locale, { isRtlLocaleFeatureEnabled: true })
    ] || enAU
  );
};

/**
 * Returns a locale string that uses the Gregorian calendar for date formatting.
 *
 * Some browser/OS combinations default `ar-SA` date formatting to the Hijri
 * calendar and Arabic-Indic digits. The app's Arabic UI should still localize
 * labels into Arabic, but date values and calendar captions must remain
 * Gregorian and use Western numerals consistently.
 */
export const getGregorianCalendarLocale = (locale?: string): string | undefined => {
  if (!locale) return locale;

  try {
    const intlLocale = new Intl.Locale(locale);
    if (intlLocale.language.toLowerCase() !== 'ar') return locale;

    return new Intl.Locale(locale, {
      calendar: 'gregory',
      numberingSystem: 'latn',
    }).toString();
  } catch {
    if (/^ar(?:-|$)/i.test(locale) && !/-u-/i.test(locale)) {
      return `${locale}-u-ca-gregory-nu-latn`;
    }

    return locale;
  }
};

/**
 * Normalizes an ISO-like timestamp string to include an explicit timezone.
 *
 * Context: Our backend sources sometimes return timestamps without a timezone
 * (e.g. "2025-08-21T23:28:31.385000") and sometimes with one (e.g. "...Z" or "+05:30").
 * To compare times reliably, we need an explicit timezone. If the string has no
 * timezone suffix, we assume UTC and append 'Z'.
 */
export const ensureIsoTimezone = (timestamp: string): string => {
  if (/z$/i.test(timestamp) || /[+-]\d{2}:?\d{2}$/.test(timestamp)) {
    return timestamp;
  }
  if (/T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?[+-]\d{2}$/.test(timestamp)) {
    return `${timestamp}:00`;
  }
  return `${timestamp}Z`;
};

/**
 * Parses a timestamp into a Date, returning undefined if input is null/undefined or invalid.
 *
 * - Clips fractional seconds to milliseconds (date-fns supports ms precision)
 * - Ensures an explicit timezone (defaults to UTC if missing)
 */
export const parseIsoMaybe = (input?: string | null): Date | undefined => {
  if (!input) return undefined;
  const trimmedFraction = input.replace(/\.(\d{3})\d+$/, '.$1');
  const normalized = ensureIsoTimezone(trimmedFraction);
  const parsed = parseISO(normalized);
  return isValid(parsed) ? parsed : undefined;
};

/**
 * A backend timestamp as an age — "3 hours ago", localized.
 *
 * Returns `''` (not a thrown error, and not "Invalid Date") for a missing or
 * unparseable input, so a notification row with a bad timestamp renders without
 * its age instead of taking the list down.
 *
 * @param locale - App locale code. Omit only where no locale is in scope; the
 *   result is then English regardless of the user's language.
 */
export const formatRelativeTimeFromNow = (
  isoTimestamp?: string | null,
  locale?: string
): string => {
  const parsed = parseIsoMaybe(isoTimestamp);
  if (!parsed) return '';
  return formatDistanceToNow(parsed, {
    addSuffix: true,
    ...(locale ? { locale: getDateFnsLocale(locale) } : {}),
  });
};

/**
 * Safely converts a Date to ISO string, returning null if the date is invalid.
 * Use this to prevent "RangeError: Invalid time value" when calling toISOString().
 *
 * Portable for the same reason `convertMomentToDateFnsFormat` below is: it reads
 * no locale, no timezone and no app state.
 */
export const safeToISOString = (date: Date): string | null => {
  if (!isValid(date)) {
    return null;
  }
  return date.toISOString();
};

const YYYYMMDD_REGEX = /^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;
const ISO_DATE_PREFIX_REGEX = /^(\d{4}-\d{2}-\d{2})(?=$|T|\s)/;

/**
 * Parses a calendar-date value as a local midnight Date.
 *
 * Use this for date-only business values whose calendar day must not shift by
 * timezone, such as birth dates, date filters, and appointment dates. Do not
 * use it for true timestamp/instant values where local timezone rendering is
 * intended.
 *
 * @param input - A Date object or date string, either date-only (e.g.,
 *                "2025-12-31") or ISO-like with a time/offset suffix
 *                (e.g., "2025-12-31T00:00:00.000Z")
 * @returns A Date object at local midnight, or undefined if input is invalid
 */
export const parseDateAsLocal = (input?: string | Date | null): Date | undefined => {
  if (!input) return undefined;

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return undefined;

    const isUtcMidnight =
      input.getUTCHours() === 0 &&
      input.getUTCMinutes() === 0 &&
      input.getUTCSeconds() === 0 &&
      input.getUTCMilliseconds() === 0;

    return isUtcMidnight
      ? new Date(input.getUTCFullYear(), input.getUTCMonth(), input.getUTCDate())
      : new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  const dateMatch = ISO_DATE_PREFIX_REGEX.exec(input.trim());
  if (!dateMatch) return undefined;

  const dateOnly = dateMatch[1];
  if (!dateOnly) return undefined;

  const parsed = parse(dateOnly, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : undefined;
};

/**
 * True if a date falls after today, comparing the calendar day only (the time
 * component is ignored, so "today" is never treated as in the future). Use for
 * guarding date-only business values such as a date of birth. Returns false for
 * an invalid Date so only genuinely future dates are flagged.
 */
export const isFutureDate = (date: Date): boolean => {
  if (Number.isNaN(date.getTime())) return false;
  const startOfTomorrow = new Date();
  startOfTomorrow.setHours(0, 0, 0, 0);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  return date.getTime() >= startOfTomorrow.getTime();
};

/**
 * Formats a date to a string with a date-fns pattern.
 * Accepts Date objects, ISO strings (with or without Z/offset), or compact YYYYMMDD strings.
 * For ISO strings, extracts the date portion to avoid timezone shift issues.
 * Returns null for falsy or malformed input.
 */
export const formatToDateOnly = (
  date?: string | Date | null,
  pattern = 'yyyy-MM-dd'
): string | null => {
  if (!date) return null;

  if (typeof date === 'string' && YYYYMMDD_REGEX.test(date)) {
    const parsed = parseDateAsLocal(
      `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
    );
    return parsed ? format(parsed, pattern) : null;
  }

  const parsed = parseDateAsLocal(date);
  return parsed && isValid(parsed) ? format(parsed, pattern) : null;
};

/**
 * Formats a force-update deadline date in a locale-aware way while fixing the timezone to UTC.
 * - Accepts Date or ISO-like string
 * - Returns null for undefined/invalid inputs
 */
export function formatUtcDate(
  input: Date | string | undefined | null,
  locale: string
): string | null {
  if (!input) return null;
  const date: Date =
    input instanceof Date ? input : new Date(ensureIsoTimezone(String(input)));
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale || 'en', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Maps moment-style format tokens to their date-fns equivalents while
 * preserving `[literal]` segments as date-fns single-quoted literals.
 *
 * Background: the `DateFormat` enum (`src/types/schema.ts`) is auto-generated
 * from the backend OpenAPI spec, and its string values are persisted in
 * `session_settings.date_format`. We can't change those values without a
 * coordinated backend migration, so this helper converts at the format
 * boundary instead — letting us keep moment-style format strings on the wire
 * while using date-fns for actual formatting.
 *
 * Supported tokens (moment → date-fns):
 *   YYYY → yyyy   YY → yy
 *   MMMM/MMM/MM/M → MMMM/MMM/MM/M           (pass through)
 *   DD → dd       D → d       Do → do        (ordinal day-of-month: 1st, 2nd…)
 *   HH/H/hh/h/mm/m/ss/s → HH/H/hh/h/mm/m/ss/s   (pass through)
 *   A → a    (uppercase AM/PM)               a → aaa   (lowercase am/pm)
 *   LTS → pp      LT → p
 *   LLLL → PPPPp  LLL → PPp   LL → PP   L → P    (approximate — see below)
 *   [literal] → 'literal'                    (internal `'` doubled as `''`)
 *
 * Localized tokens (`L`, `LT`, `LTS`, `LL`, `LLL`, `LLLL`) are NOT exact
 * width-for-width matches across libraries. Both moment and date-fns produce
 * locale-defined output, but the locale tables differ — e.g. moment `LL` is
 * "September 4, 1986" (full month) in `en`, while date-fns `PP` is
 * "Sep 4, 1986" (abbreviated month) in `en-US`. Treat the mappings here as
 * "closest standard equivalent". For call sites where the exact rendering
 * matters (date pickers, billing labels, etc.), spell out a literal
 * date-fns pattern (e.g. `'MMMM d, yyyy'`) at the call site instead of
 * routing through `LL`.
 *
 * Tokens used elsewhere in moment (e.g. `dddd`, `Q`, `Z`, `X`) are not
 * recognized today — no call site in this repo uses them. Add to the token
 * map below if a new use case arises.
 *
 * @see https://date-fns.org/v4/docs/format
 * @see https://momentjs.com/docs/#/displaying/format/
 */
const MOMENT_TO_DATE_FNS_FORMAT_TOKENS: Readonly<Record<string, string>> = {
  YYYY: 'yyyy',
  YY: 'yy',
  MMMM: 'MMMM',
  MMM: 'MMM',
  MM: 'MM',
  M: 'M',
  Do: 'do',
  DD: 'dd',
  D: 'd',
  HH: 'HH',
  H: 'H',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  m: 'm',
  ss: 'ss',
  s: 's',
  A: 'a',
  a: 'aaa',
  LTS: 'pp',
  LT: 'p',
  LLLL: 'PPPPp',
  LLL: 'PPp',
  LL: 'PP',
  L: 'P',
};

// Longest-first alternation guards against e.g. `D` swallowing `DD` or `L`
// swallowing `LTS`. `Do` (ordinal day) must precede `DD` and `D` so the `D`
// branch can't strip the leading letter first. Bracketed `[literal]` segments
// capture into group 1 so we can re-emit them as date-fns single-quoted literals.
const MOMENT_TOKEN_REGEX =
  /\[([^\]]*)\]|LLLL|LLL|LL|LTS|LT|L|YYYY|YY|MMMM|MMM|MM|M|Do|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g;

export const convertMomentFormat = (momentFormat: string): string =>
  momentFormat.replace(
    MOMENT_TOKEN_REGEX,
    (match: string, literal: string | undefined) => {
      if (literal !== undefined) {
        // Date-fns escapes an embedded single quote as `''` inside a literal.
        return `'${literal.replace(/'/g, "''")}'`;
      }
      return MOMENT_TO_DATE_FNS_FORMAT_TOKENS[match] ?? match;
    }
  );

/**
 * Formats a date using a moment-style format string.
 *
 * Thin wrapper around date-fns `format` that runs `convertMomentFormat` over
 * the pattern first. Use this for any call site that previously read a
 * moment-style format string from the backend-owned `DateFormat` enum or
 * `session_settings.date_format`.
 */
export const formatWithMomentToken = (
  date: Date,
  momentFormat: string,
  locale?: Locale
): string =>
  format(date, convertMomentFormat(momentFormat), locale ? { locale } : undefined);

/**
 * Formats a non-negative duration in seconds as either `HH:mm:ss` (default)
 * or `mm:ss` when `{ shorten: true }` AND the duration is under one hour.
 *
 * HH is computed from the raw second count, so durations ≥ 24 hours
 * render correctly (e.g. 90000s → `25:00:00`) — a UTC-zero-anchored date
 * formatter would silently wrap at midnight. No production caller
 * approaches the 24-hour boundary today (timer durations are capped at
 * 3 hours).
 *
 * Negative, NaN, and non-finite inputs are clamped to zero. Fractional
 * seconds are floored.
 */
export const formatDurationFromSeconds = (
  seconds: number,
  options?: { shorten?: boolean }
): string => {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');

  if (options?.shorten && total < 3600) {
    return `${mm}:${ss}`;
  }
  const hh = String(hours).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

/**
 * Convert a moment-style format string — what the backend stores in
 * `session_settings.date_format` — to the date-fns equivalent.
 *
 * A pure string transform: no locale, no timezone, no app state. The rest of
 * `@/lib/parseTimeStamp` stays app-side because it reads the account timezone
 * via `getTimezone()`; only this function is portable, so only it moved.
 */
export const convertMomentToDateFnsFormat = (momentFormat: DateFormat): string => {
  return momentFormat
    .replace(/DD/g, 'dd') // Day with leading zero
    .replace(/D/g, 'd') // Day without leading zero
    .replace(/YYYY/g, 'yyyy') // Full year
    .replace(/YY/g, 'yy'); // Short year
};
